import { computed, ref } from "vue";
import { useFlag, useFlagsStatus } from "@unleash/proxy-client-vue";

/** Unleash flag for the customer release notes: the public page and the after-release popup (RDT-286). */
export const CUSTOMER_RELEASE_NOTES_FLAG = "customer_release_notes_enabled";

/**
 * Unleash flag for the distributor profile reclaim (RDT-113/RDT-333): the reclaim actions in the
 * sub-account management and the distributor's purchases table, and the reclaim entries in the
 * transaction type filter. Rows the backend already wrote keep their labels regardless of the flag.
 */
export const PROFILE_RECLAIM_FLAG = "profile_reclaim_enabled";

/**
 * Unleash flag for generated pseudonyms in anonymous projects (RDT-402).
 *
 * Gated because the feature spans three services and only becomes usable when the last one lands.
 * profile-administration already honours the setting when it creates a participant, but until the
 * questionnaire service reads it, a participant in a GENERATED project would still be shown a
 * pseudonym field, type something into it, and have it silently discarded. Showing the trainer a
 * choice that behaves like that is worse than not shipping it, so the radio stays hidden on the
 * deployed stages until the chain is complete.
 */
export const GENERATED_PSEUDONYMS_FLAG = "generated_pseudonyms_enabled";

/**
 * An Unleash flag, reactive, usable anywhere below the `FlagProvider` in `App.vue`.
 *
 * Where the app runs **without** a flag provider (`VITE_APP_USE_UNLEASH=false` — localhost and the
 * CI e2e stack) `useFlag` has nothing to inject and would report `false`, hiding the feature
 * outright. Those environments are treated as **enabled** instead, so local development and the
 * Cypress suite always see the whole app. Only the deployed stages (dev, staging, production) are
 * actually gated, which is where the rollout is being steered from anyway.
 */
export function useFeatureFlag(name: string) {
    if (import.meta.env.VITE_APP_USE_UNLEASH !== "true") return ref(true);
    return useFlag(name);
}

/**
 * Whether the flags have actually been fetched, so a `false` flag can be told apart from "the client
 * has not answered yet". Needed wherever *acting* on a disabled flag is visible to the user (a
 * redirect, say) rather than merely not rendering something. `true` immediately where there is no
 * provider, and also once the client reports an error — an unreachable Unleash must not leave a page
 * hanging forever.
 */
export function useFlagsResolved() {
    if (import.meta.env.VITE_APP_USE_UNLEASH !== "true") return ref(true);
    const { flagsReady, flagsError } = useFlagsStatus();
    return computed(() => Boolean(flagsReady?.value) || Boolean(flagsError?.value));
}
